import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Download, Battery, Activity, Moon, Heart, ChevronLeft, ChevronRight, FileText, MapPin, Briefcase, Home, Footprints, BedDouble, Flame, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LevelSlider({ value, onChange, icon: Icon, label, color, lowLabel, highLabel }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        <span className="text-sm text-muted-foreground">{value || '-'}/10</span>
      </div>
      <input
        type="range"
        min="1"
        max="10"
        value={value || 5}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-secondary rounded-full appearance-none cursor-pointer accent-primary"
        data-testid={`${label.toLowerCase().replace(' ', '-')}-slider`}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{lowLabel || 'Low'}</span>
        <span>{highLabel || 'High'}</span>
      </div>
    </div>
  );
}

function TagSelector({ selected, options, onChange, label }) {
  const toggleTag = (tag) => {
    if (selected.includes(tag)) {
      onChange(selected.filter(t => t !== tag));
    } else {
      onChange([...selected, tag]);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium text-foreground mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => toggleTag(tag)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              selected.includes(tag)
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }`}
            data-testid={`tag-${tag.toLowerCase().replace(' ', '-')}`}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

function BodyPartSelector({ selected, onChange }) {
  const bodyParts = [
    { id: 'head', label: 'Head', position: 'top-[8%] left-1/2 -translate-x-1/2' },
    { id: 'neck', label: 'Neck', position: 'top-[18%] left-1/2 -translate-x-1/2' },
    { id: 'shoulders', label: 'Shoulders', position: 'top-[22%] left-1/2 -translate-x-1/2' },
    { id: 'chest', label: 'Chest', position: 'top-[30%] left-1/2 -translate-x-1/2' },
    { id: 'upper-back', label: 'Upper Back', position: 'top-[28%] left-[75%]' },
    { id: 'lower-back', label: 'Lower Back', position: 'top-[42%] left-[75%]' },
    { id: 'stomach', label: 'Stomach', position: 'top-[42%] left-1/2 -translate-x-1/2' },
    { id: 'hips', label: 'Hips', position: 'top-[52%] left-1/2 -translate-x-1/2' },
    { id: 'left-arm', label: 'Left Arm', position: 'top-[35%] left-[20%]' },
    { id: 'right-arm', label: 'Right Arm', position: 'top-[35%] left-[80%]' },
    { id: 'hands', label: 'Hands', position: 'top-[50%] left-[15%]' },
    { id: 'left-leg', label: 'Left Leg', position: 'top-[70%] left-[35%]' },
    { id: 'right-leg', label: 'Right Leg', position: 'top-[70%] left-[65%]' },
    { id: 'knees', label: 'Knees', position: 'top-[75%] left-1/2 -translate-x-1/2' },
    { id: 'feet', label: 'Feet', position: 'top-[92%] left-1/2 -translate-x-1/2' },
    { id: 'joints', label: 'Joints (General)', position: 'hidden' },
    { id: 'whole-body', label: 'Whole Body', position: 'hidden' },
  ];

  const togglePart = (partId) => {
    if (selected.includes(partId)) {
      onChange(selected.filter(p => p !== partId));
    } else {
      onChange([...selected, partId]);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="h-4 w-4 text-red-500" />
        <p className="text-sm font-medium text-foreground">Where does it hurt?</p>
      </div>
      
      {/* Quick select buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {bodyParts.map(part => (
          <button
            key={part.id}
            type="button"
            onClick={() => togglePart(part.id)}
            className={`px-3 py-1.5 text-xs rounded-full transition-all ${
              selected.includes(part.id)
                ? 'bg-red-500 text-white shadow-sm'
                : 'bg-secondary text-secondary-foreground hover:bg-red-100'
            }`}
            data-testid={`body-part-${part.id}`}
          >
            {part.label}
          </button>
        ))}
      </div>
      
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Selected: {selected.map(id => bodyParts.find(p => p.id === id)?.label).join(', ')}
        </p>
      )}
    </div>
  );
}

function MiniChart({ data, dataKey, color }) {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0).slice(0, 7).reverse();
  const max = 10;
  
  return (
    <div className="flex items-end gap-1 h-12">
      {values.map((val, i) => (
        <div
          key={i}
          className={`w-4 rounded-t ${color}`}
          style={{ height: `${(val / max) * 100}%`, opacity: val ? 1 : 0.2 }}
        />
      ))}
    </div>
  );
}

export default function SymptomTracker() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [energy, setEnergy] = useState(null);
  const [pain, setPain] = useState(null);
  const [painLocations, setPainLocations] = useState([]);
  const [painLocationSeverity, setPainLocationSeverity] = useState({});
  const [mood, setMood] = useState(null);
  const [sleep, setSleep] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [notes, setNotes] = useState('');
  const [isFlareDay, setIsFlareDay] = useState(false);
  const [isSymptomFree, setIsSymptomFree] = useState(false);
  // Functional impact (10 = no impact, 1 = severe impact)
  const [impactMobility, setImpactMobility] = useState(null);
  const [impactWork, setImpactWork] = useState(null);
  const [impactDaily, setImpactDaily] = useState(null);
  const [impactSleep, setImpactSleep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImpact, setShowImpact] = useState(false);

  const symptomOptions = ['Fatigue', 'Brain fog', 'Pain', 'Headache', 'Nausea', 'Dizziness', 'Anxiety', 'Low mood', 'Insomnia', 'Sensory overload'];
  const triggerOptions = ['Stress', 'Poor sleep', 'Weather', 'Food', 'Exercise', 'Social event', 'Work', 'Noise', 'Light', 'Skipped meds'];

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/symptoms?days=30`, { headers: { Authorization: `Bearer ${token}` } });
      setLogs(res.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const log = logs.find(l => l.date === selectedDate);
    if (log) {
      setEnergy(log.energy_level);
      setPain(log.pain_level);
      setPainLocations(log.pain_locations || []);
      setPainLocationSeverity(log.pain_location_severity || {});
      setMood(log.mood_level);
      setSleep(log.sleep_quality);
      setSymptoms(log.symptoms || []);
      setTriggers(log.triggers || []);
      setNotes(log.notes || '');
      setIsFlareDay(log.is_flare_day || false);
      setIsSymptomFree(log.is_symptom_free || false);
      setImpactMobility(log.impact_mobility);
      setImpactWork(log.impact_work);
      setImpactDaily(log.impact_daily_activities);
      setImpactSleep(log.impact_sleep_impact);
      // Show impact section if any impact was previously recorded
      setShowImpact(!!(log.impact_mobility || log.impact_work || log.impact_daily_activities || log.impact_sleep_impact));
    } else {
      setEnergy(null);
      setPain(null);
      setPainLocations([]);
      setPainLocationSeverity({});
      setMood(null);
      setSleep(null);
      setSymptoms([]);
      setTriggers([]);
      setNotes('');
      setIsFlareDay(false);
      setIsSymptomFree(false);
      setImpactMobility(null);
      setImpactWork(null);
      setImpactDaily(null);
      setImpactSleep(null);
      setShowImpact(false);
    }
  }, [selectedDate, logs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/symptoms`, {
        date: selectedDate,
        energy_level: energy,
        pain_level: pain,
        pain_locations: painLocations,
        pain_location_severity: painLocationSeverity,
        mood_level: mood,
        sleep_quality: sleep,
        symptoms,
        triggers,
        notes,
        is_flare_day: isFlareDay,
        is_symptom_free: isSymptomFree,
        impact_mobility: impactMobility,
        impact_work: impactWork,
        impact_daily_activities: impactDaily,
        impact_sleep_impact: impactSleep
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved!');
      fetchLogs();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/symptoms/export?days=90`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'symptoms-export.csv';
      a.click();
      toast.success(`Exported ${res.data.count} days of data`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    if (d <= new Date()) {
      setSelectedDate(d.toISOString().split('T')[0]);
    }
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const displayDate = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-safe" data-testid="symptom-tracker-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" /><span>Back</span>
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1" />CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/healthcare-export')} data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-1" />PDF
            </Button>
          </div>
        </div>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Symptom Tracker</h1>
          <p className="mt-2 text-muted-foreground">Track patterns to understand your body</p>
        </div>

        {/* Date Selector */}
        <div className="mt-6 flex items-center justify-between card-soft" data-testid="date-selector">
          <button onClick={() => changeDate(-1)} className="p-2 hover:bg-secondary rounded-full" data-testid="prev-day-btn">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <p className="font-medium text-foreground" data-testid="selected-date">{displayDate}</p>
            {isToday && <span className="text-xs text-primary">Today</span>}
          </div>
          <button onClick={() => changeDate(1)} disabled={isToday} className={`p-2 rounded-full ${isToday ? 'opacity-30' : 'hover:bg-secondary'}`} data-testid="next-day-btn">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Quick Stats */}
        {logs.length > 0 && (
          <div className="mt-4 grid grid-cols-4 gap-2" data-testid="quick-stats">
            <div className="card-soft p-3 text-center">
              <p className="text-xs text-muted-foreground">Energy</p>
              <MiniChart data={logs} dataKey="energy_level" color="bg-green-400" />
            </div>
            <div className="card-soft p-3 text-center">
              <p className="text-xs text-muted-foreground">Pain</p>
              <MiniChart data={logs} dataKey="pain_level" color="bg-red-400" />
            </div>
            <div className="card-soft p-3 text-center">
              <p className="text-xs text-muted-foreground">Mood</p>
              <MiniChart data={logs} dataKey="mood_level" color="bg-blue-400" />
            </div>
            <div className="card-soft p-3 text-center">
              <p className="text-xs text-muted-foreground">Sleep</p>
              <MiniChart data={logs} dataKey="sleep_quality" color="bg-purple-400" />
            </div>
          </div>
        )}

        {/* Day Type Markers */}
        <div className="mt-4 flex gap-2" data-testid="day-type-markers">
          <button
            onClick={() => { setIsFlareDay(!isFlareDay); if (!isFlareDay) setIsSymptomFree(false); }}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              isFlareDay 
                ? 'bg-red-500 text-white' 
                : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
            }`}
            data-testid="flare-day-btn"
          >
            <Flame className="h-4 w-4" />
            Flare Day
          </button>
          <button
            onClick={() => { setIsSymptomFree(!isSymptomFree); if (!isSymptomFree) setIsFlareDay(false); }}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              isSymptomFree 
                ? 'bg-green-500 text-white' 
                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
            }`}
            data-testid="symptom-free-btn"
          >
            <CheckCircle className="h-4 w-4" />
            Symptom-Free
          </button>
        </div>

        {/* Tracking Form */}
        <div className="mt-6 space-y-6">
          <div className="card-soft space-y-6">
            <LevelSlider value={energy} onChange={setEnergy} icon={Battery} label="Energy Level" color="text-green-500" lowLabel="Exhausted" highLabel="Energized" />
            <LevelSlider value={pain} onChange={setPain} icon={Activity} label="Pain Level" color="text-red-500" lowLabel="None" highLabel="Severe" />
            
            {/* Body Part Pain Selector - shows when pain > 0 */}
            {pain && pain > 1 && (
              <BodyPartSelector selected={painLocations} onChange={setPainLocations} />
            )}
            
            <LevelSlider value={mood} onChange={setMood} icon={Heart} label="Mood" color="text-blue-500" lowLabel="Low" highLabel="Great" />
            <LevelSlider value={sleep} onChange={setSleep} icon={Moon} label="Sleep Quality" color="text-purple-500" lowLabel="Poor" highLabel="Restful" />
          </div>

          <div className="card-soft">
            <TagSelector selected={symptoms} options={symptomOptions} onChange={setSymptoms} label="Symptoms" />
          </div>

          <div className="card-soft">
            <TagSelector selected={triggers} options={triggerOptions} onChange={setTriggers} label="Possible Triggers" />
          </div>

          {/* Functional Impact Section */}
          <div className="card-soft">
            <button 
              onClick={() => setShowImpact(!showImpact)}
              className="w-full flex items-center justify-between text-sm font-medium text-foreground"
              data-testid="toggle-impact-btn"
            >
              <span className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-amber-500" />
                Functional Impact (Optional)
              </span>
              <ChevronRight className={`h-4 w-4 transition-transform ${showImpact ? 'rotate-90' : ''}`} />
            </button>
            
            {showImpact && (
              <div className="mt-4 space-y-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">How did symptoms affect your day? (10 = no impact, 1 = severe)</p>
                <LevelSlider 
                  value={impactMobility} 
                  onChange={setImpactMobility} 
                  icon={Footprints} 
                  label="Mobility" 
                  color="text-amber-500" 
                  lowLabel="Severe" 
                  highLabel="None" 
                />
                <LevelSlider 
                  value={impactWork} 
                  onChange={setImpactWork} 
                  icon={Briefcase} 
                  label="Work Capacity" 
                  color="text-blue-500" 
                  lowLabel="Severe" 
                  highLabel="None" 
                />
                <LevelSlider 
                  value={impactDaily} 
                  onChange={setImpactDaily} 
                  icon={Home} 
                  label="Daily Activities" 
                  color="text-green-500" 
                  lowLabel="Severe" 
                  highLabel="None" 
                />
                <LevelSlider 
                  value={impactSleep} 
                  onChange={setImpactSleep} 
                  icon={BedDouble} 
                  label="Sleep (symptom impact)" 
                  color="text-purple-500" 
                  lowLabel="Severe" 
                  highLabel="None" 
                />
              </div>
            )}
          </div>

          <div className="card-soft">
            <p className="text-sm font-medium text-foreground mb-2">Notes</p>
            <Textarea
              placeholder="Any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px]"
              data-testid="notes-input"
            />
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full btn-primary" data-testid="save-entry-btn">
            {saving ? 'Saving...' : 'Save Entry'}
          </Button>
          
          {/* Healthcare Export CTA */}
          <button
            onClick={() => navigate('/healthcare-export')}
            className="w-full card-soft bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 hover:border-blue-300 flex items-center justify-between"
            data-testid="healthcare-export-cta"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-500" />
              <div className="text-left">
                <p className="font-medium text-foreground text-sm">Healthcare Export</p>
                <p className="text-xs text-muted-foreground">Generate detailed PDF reports</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-blue-400" />
          </button>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
